import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { adminCmsAPI } from '../../lib/cmsApi';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  GripVertical,
  Eye,
  EyeOff,
  ExternalLink,
  Image,
} from 'lucide-react';

const CmsServices = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [editingService, setEditingService] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mediaList, setMediaList] = useState([]);
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  const iconOptions = ['Droplets', 'Building2', 'Route', 'Trees', 'FileText', 'FolderKanban'];

  const defaultService = {
    title: '',
    slug: '',
    short_description: '',
    full_description: '',
    icon: 'Droplets',
    image_url: '',
    bullet_points: [],
    objective: '',
    approaches: [],
    order: 0,
    is_featured: true,
    status: 'publie',
  };

  useEffect(() => {
    if (user?.role !== 'administrateur' && user?.role !== 'employe') {
      navigate('/portail');
      return;
    }
    fetchServices();
    fetchMedia();
  }, [user, navigate]);

  const fetchServices = async () => {
    try {
      const response = await adminCmsAPI.listServices();
      setServices(response.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des services');
    } finally {
      setLoading(false);
    }
  };

  const fetchMedia = async () => {
    try {
      const response = await adminCmsAPI.listMedia('services');
      setMediaList(response.data || []);
    } catch (error) {
      console.error('Error fetching media:', error);
    }
  };

  const handleSave = async () => {
    if (!editingService.title || !editingService.slug) {
      toast.error('Le titre et le slug sont requis');
      return;
    }

    setSaving(true);
    try {
      if (editingService.id) {
        await adminCmsAPI.updateService(editingService.id, editingService);
        toast.success('Service mis à jour');
      } else {
        await adminCmsAPI.createService(editingService);
        toast.success('Service créé');
      }
      setShowDialog(false);
      setEditingService(null);
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce service?')) return;
    
    try {
      await adminCmsAPI.deleteService(id);
      toast.success('Service supprimé');
      fetchServices();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleStatus = async (service) => {
    try {
      const newStatus = service.status === 'publie' ? 'brouillon' : 'publie';
      await adminCmsAPI.updateService(service.id, { status: newStatus });
      toast.success(`Service ${newStatus === 'publie' ? 'publié' : 'mis en brouillon'}`);
      fetchServices();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleBulletPointsChange = (text) => {
    const points = text.split('\n').filter(p => p.trim());
    setEditingService({ ...editingService, bullet_points: points });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#F9B438]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="cms-services">
      {/* Header */}
      <header className="bg-[#1D1D1B] text-white py-6">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/cms">
                <Button variant="ghost" size="icon" className="text-white/70 hover:text-white">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div>
                <h1 className="font-heading text-2xl font-bold">Expertises / Services</h1>
                <p className="text-white/60 text-sm">{services.length} service(s)</p>
              </div>
            </div>
            <Button 
              onClick={() => {
                setEditingService({ ...defaultService, order: services.length });
                setShowDialog(true);
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau service
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="space-y-4">
          {services.map((service, index) => (
            <Card key={service.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="cursor-move text-neutral-400">
                    <GripVertical className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-bold text-[#F9B438]">
                        {String(index + 1).padStart(2, '0')}.
                      </span>
                      <h3 className="font-semibold text-[#1D1D1B]">{service.title}</h3>
                      <Badge variant={service.status === 'publie' ? 'default' : 'secondary'}>
                        {service.status === 'publie' ? 'Publié' : 'Brouillon'}
                      </Badge>
                      {service.is_featured && (
                        <Badge variant="outline">Vedette</Badge>
                      )}
                    </div>
                    <p className="text-sm text-neutral-500 line-clamp-1">{service.short_description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleStatus(service)}
                      title={service.status === 'publie' ? 'Mettre en brouillon' : 'Publier'}
                    >
                      {service.status === 'publie' ? (
                        <Eye className="w-4 h-4 text-green-600" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-neutral-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setEditingService(service);
                        setShowDialog(true);
                      }}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(service.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {services.length === 0 && (
            <Card>
              <CardContent className="p-12 text-center">
                <p className="text-neutral-500 mb-4">Aucun service pour le moment</p>
                <Button 
                  onClick={() => {
                    setEditingService({ ...defaultService });
                    setShowDialog(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Créer le premier service
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingService?.id ? 'Modifier le service' : 'Nouveau service'}
            </DialogTitle>
          </DialogHeader>
          
          {editingService && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Titre *</Label>
                  <Input
                    value={editingService.title}
                    onChange={(e) => {
                      const title = e.target.value;
                      setEditingService({
                        ...editingService,
                        title,
                        slug: editingService.id ? editingService.slug : generateSlug(title),
                      });
                    }}
                    placeholder="Gestion intégrée des eaux"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Slug *</Label>
                  <Input
                    value={editingService.slug}
                    onChange={(e) => setEditingService({ ...editingService, slug: e.target.value })}
                    placeholder="gestion-integree-eaux"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description courte</Label>
                <Textarea
                  value={editingService.short_description}
                  onChange={(e) => setEditingService({ ...editingService, short_description: e.target.value })}
                  placeholder="Résumé du service en une ou deux phrases..."
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Description complète</Label>
                <Textarea
                  value={editingService.full_description}
                  onChange={(e) => setEditingService({ ...editingService, full_description: e.target.value })}
                  placeholder="Description détaillée du service..."
                  rows={4}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Icône</Label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    value={editingService.icon}
                    onChange={(e) => setEditingService({ ...editingService, icon: e.target.value })}
                  >
                    {iconOptions.map((icon) => (
                      <option key={icon} value={icon}>{icon}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Statut</Label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    value={editingService.status}
                    onChange={(e) => setEditingService({ ...editingService, status: e.target.value })}
                  >
                    <option value="publie">Publié</option>
                    <option value="brouillon">Brouillon</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Image</Label>
                <div className="flex gap-2">
                  <Input
                    value={editingService.image_url || ''}
                    onChange={(e) => setEditingService({ ...editingService, image_url: e.target.value })}
                    placeholder="URL de l'image"
                    className="flex-1"
                  />
                  <Button variant="outline" onClick={() => setShowMediaPicker(true)}>
                    <Image className="w-4 h-4 mr-2" />
                    Choisir
                  </Button>
                </div>
                {editingService.image_url && (
                  <img 
                    src={editingService.image_url} 
                    alt="Aperçu"
                    className="w-32 h-24 object-cover rounded mt-2"
                  />
                )}
              </div>

              <div className="space-y-2">
                <Label>Points clés (un par ligne)</Label>
                <Textarea
                  value={editingService.bullet_points?.join('\n') || ''}
                  onChange={(e) => handleBulletPointsChange(e.target.value)}
                  placeholder="Modélisation hydraulique&#10;Gestion des eaux pluviales&#10;Études de capacité"
                  rows={5}
                />
              </div>

              <div className="space-y-2">
                <Label>Objectif</Label>
                <Input
                  value={editingService.objective || ''}
                  onChange={(e) => setEditingService({ ...editingService, objective: e.target.value })}
                  placeholder="L'objectif principal de ce service..."
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingService.is_featured}
                    onChange={(e) => setEditingService({ ...editingService, is_featured: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Afficher en vedette sur la page d'accueil</span>
                </label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingService?.id ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Media Picker Dialog */}
      <Dialog open={showMediaPicker} onOpenChange={setShowMediaPicker}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Choisir une image</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-4 gap-4 py-4 max-h-[60vh] overflow-y-auto">
            {mediaList.filter(m => m.file_type === 'image').map((media) => (
              <div
                key={media.id}
                className="aspect-square bg-neutral-100 rounded overflow-hidden cursor-pointer hover:ring-2 hover:ring-[#F9B438] transition-all"
                onClick={() => {
                  setEditingService({ ...editingService, image_url: media.url });
                  setShowMediaPicker(false);
                }}
              >
                <img
                  src={media.url}
                  alt={media.alt_text || media.original_filename}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
            {mediaList.filter(m => m.file_type === 'image').length === 0 && (
              <p className="col-span-4 text-center text-neutral-500 py-8">
                Aucune image disponible. Téléversez d'abord des images dans la médiathèque.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMediaPicker(false)}>
              Annuler
            </Button>
            <Link to="/cms/media">
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Téléverser une image
              </Button>
            </Link>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CmsServices;
